import { z } from 'zod';

/** Server-scoped evidence. Text fields are untrusted document data, not instructions. */
export interface TaxGuideMappingEvidence {
  lines: {
    taxLineId: string;
    taxCode: string | null;
    description: string;
    currentSlotCode?: string | null;
    selectedManually?: boolean;
    candidateSlotCodes: string[];
    officialReferences: string[];
    /** Server-derived context from the same statutory document; never inferred from a bare code. */
    activeDebtPayrollContext?: {
      isRpaContribuinteIndividual: false;
      taxDescription: string;
      companionTaxCodes: string[];
    };
  }[];
  /** Only active, postable credit-liability candidates validated by the server. */
  accounts: {
    versionId: string;
    glAccountId: string;
    code: string;
    name: string;
  }[];
  /** Active postable expense accounts available for an explicit ordinary-fee suggestion. */
  classificationAccounts: {
    versionId: string;
    glAccountId: string;
    code: string;
    name: string;
  }[];
  missingMappings: {
    versionId: string;
    slotCode: string;
    slotName: string;
  }[];
}

export const TaxGuideMappingProposalSchema = z
  .object({
    lineDecisions: z.array(
      z
        .object({
          taxLineId: z.string().min(1),
          slotCode: z.string().min(1),
          evidenceQuote: z.string().trim().min(1),
          reason: z.string().trim().min(1),
        })
        .strict(),
    ),
    accountDecisions: z.array(
      z
        .object({
          versionId: z.string().min(1),
          slotCode: z.string().min(1),
          glAccountId: z.string().min(1),
          evidenceQuote: z.string().trim().min(1),
          reason: z.string().trim().min(1),
        })
        .strict(),
    ),
    // OpenAI structured output requires every object property to appear in `required`.
    // Empty means there is no suggestion; persisted legacy run metadata remains separately lenient.
    taxSuggestions: z.array(
      z
        .object({
          taxLineId: z.string().min(1),
          suggestedSlotCode: z.string().min(1).nullable(),
          suggestedTreatment: z.literal('CLASSIFY').nullable(),
          suggestedGlAccountId: z.string().min(1).nullable(),
          accountEvidenceQuote: z.string().trim().min(1).nullable(),
          evidenceQuote: z.string().trim().min(1),
          reason: z.string().trim().min(1),
        })
        .strict(),
    ),
    unresolved: z.array(
      z
        .object({
          taxLineId: z.string().min(1).nullable(),
          reason: z.string().trim().min(1),
        })
        .strict(),
    ),
  })
  .strict();

/** A proposal only. The server revalidates permissions and current mappings before applying. */
export type TaxGuideMappingProposal = z.infer<typeof TaxGuideMappingProposalSchema>;
